import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import useAuth from '../auth/feature/auth';
import { pricingService } from './service/pricing.service';
import { ROUTER_LINKS } from '../common/consts/app-keys.const';

const tealCheckIcon = (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0ea5c9" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const TrialSuccessModal = ({ expiresAt, onDismiss }: { expiresAt: Date; onDismiss: () => void }) => {
  const navigate = useNavigate();
  const features = [
    'Unlimited active queues & participants',
    'Unlimited teams & co-admins',
    'Discord & Slack webhook notifications',
    'QR code scan-to-join',
    'Password-protected queues',
    'Multi-team support',
  ];

  const endDate = expiresAt.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] px-[16px]">
      <div className="bg-white dark:bg-[#1C1C1F] rounded-[20px] border border-[#BCEBFB] dark:border-[#0ea5c9]/30 shadow-[0_20px_60px_rgba(0,0,0,0.18)] w-full max-w-[420px] p-[32px] flex flex-col items-center text-center">
        <div className="w-[56px] h-[56px] rounded-full bg-[#0ea5c9] flex items-center justify-center mb-[20px]">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>

        <h2 className="font-display text-[24px] font-bold text-base-black dark:text-white mb-[8px]">
          Your trial has started!
        </h2>

        <span className="inline-flex items-center gap-[6px] px-[12px] py-[4px] rounded-[32px] bg-[#F0F9FF] border border-[#BCEBFB] font-body text-[12px] font-semibold text-[#0ea5c9] mb-[16px]">
          <span className="w-[5px] h-[5px] rounded-full bg-[#0ea5c9]" />
          14 days free · Ends {endDate}
        </span>

        <p className="font-body text-[14px] text-[#818185] dark:text-[#A0A0A3] mb-[24px]">
          You have full Pro access. Here's what's unlocked:
        </p>

        <ul className="w-full flex flex-col gap-[8px] mb-[28px]">
          {features.map((f) => (
            <li key={f} className="flex items-center gap-[10px] text-left">
              <span className="flex-shrink-0 w-[18px] h-[18px] rounded-full bg-[#F0F9FF] dark:bg-[#0ea5c9]/10 flex items-center justify-center">
                {tealCheckIcon}
              </span>
              <span className="font-body text-[13px] text-base-black dark:text-white">{f}</span>
            </li>
          ))}
        </ul>

        <div className="w-full flex flex-col gap-[8px]">
          <button
            type="button"
            onClick={onDismiss}
            className="w-full h-[44px] rounded-[32px] bg-base-black text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] font-body text-[14px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB] transition-all"
          >
            Start exploring
          </button>
          <button
            type="button"
            onClick={() => { onDismiss(); navigate(ROUTER_LINKS.PRICING); }}
            className="w-full h-[44px] rounded-[32px] border border-[#0ea5c9] text-[#0ea5c9] font-body text-[14px] font-medium hover:bg-[#F0F9FF] transition-all"
          >
            Subscribe now
          </button>
        </div>
      </div>
    </div>
  );
};

const checkIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0ea5c9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const dashIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#BFBFC0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

interface Feature {
  label: string;
  free: string | boolean;
  pro: string | boolean;
  enterprise: string | boolean;
}

const CONTACT_EMAIL = 'contact@squarebitstudios.com';

export const PricingPage = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [trialSuccessExpiry, setTrialSuccessExpiry] = React.useState<Date | null>(null);
  const [billing, setBilling] = React.useState<'monthly' | 'annual'>('monthly');
  const [enterpriseCopied, setEnterpriseCopied] = React.useState(false);

  const isPro = user?.isPro === true;
  const isTrial = user?.subscriptionTier === 'trial';
  const showTrial = !isAuthenticated || user?.hasFreeTrialAbility;

  const duration = billing === 'annual' ? '1y' : '1m';

  const { data: monthlyPrice } = useQuery({
    queryKey: ['price', '1m'],
    queryFn: () => pricingService.getPrice('1m')
  });

  const { data: annualPrice } = useQuery({
    queryKey: ['price', '1y'],
    queryFn: () => pricingService.getPrice('1y')
  });

  const { mutate: checkout, isPending: checkoutPending } = useMutation({
    mutationFn: () => pricingService.getCheckoutSession(duration),
    onSuccess: (data) => {
      if (data?.url) {
        window.location.href = data.url;
      }
    },
    onError: () => {
      toast.error(t('pricing.checkoutFailed'));
    }
  });

  const { mutate: startTrial, isPending: trialPending } = useMutation({
    mutationFn: () => pricingService.startFreeTrial(),
    onSuccess: async (data: any) => {
      await checkAuth();
      setTrialSuccessExpiry(new Date(data.expiresAt));
    },
    onError: () => {
      toast.error(t('pricing.trialFailed'));
    }
  });

  const handleProClick = () => {
    if (!isAuthenticated) {
      navigate(ROUTER_LINKS.SIGNUP);
      return;
    }
    checkout();
  };

  const handleFreeClick = () => {
    if (!isAuthenticated) {
      navigate(ROUTER_LINKS.SIGNUP);
      return;
    }
    navigate(ROUTER_LINKS.DASHBOARD);
  };

  const handleTrialClick = () => {
    if (!isAuthenticated) {
      navigate(ROUTER_LINKS.SIGNUP);
      return;
    }
    startTrial();
  };

  const monthlyAmount = monthlyPrice?.amount ?? 5;
  const annualAmount = annualPrice?.amount ?? 48;
  const displayPrice = billing === 'annual' ? Math.round(annualAmount / 12) : monthlyAmount;
  const isAnnual = billing === 'annual';

  const isEnterprise = user?.isEnterprise === true;
  const unlimited = t('pricing.unlimited');
  const features: Feature[] = [
    { label: t('pricing.featureActiveReviews'), free: '5', pro: unlimited, enterprise: unlimited },
    { label: t('pricing.featureParticipants'), free: '15', pro: unlimited, enterprise: unlimited },
    { label: t('pricing.featureTeams'), free: '1', pro: unlimited, enterprise: unlimited },
    { label: t('pricing.featureQueue'), free: true, pro: true, enterprise: true },
    { label: t('pricing.featureCoAdmins'), free: false, pro: true, enterprise: true },
    { label: t('pricing.featurePasswords'), free: false, pro: true, enterprise: true },
    { label: t('pricing.featureQrJoin'), free: false, pro: true, enterprise: true },
    { label: t('pricing.featureMultiTeam'), free: false, pro: true, enterprise: true },
    { label: t('pricing.featureDiscord'), free: false, pro: true, enterprise: true },
    { label: t('pricing.featureSupport'), free: false, pro: true, enterprise: true },
    { label: 'Company branding & subsites', free: false, pro: false, enterprise: true },
    { label: 'QR code check-in', free: false, pro: false, enterprise: true },
    { label: 'Queue templates', free: false, pro: false, enterprise: true },
    { label: 'Wait time estimates', free: false, pro: false, enterprise: true },
    { label: 'Feature toggles', free: false, pro: false, enterprise: true },
    { label: 'Dedicated company dashboard', free: false, pro: false, enterprise: true },
  ];

  return (
    <div className="flex flex-col items-center py-[40px] md:py-[60px] px-[16px]">
      <h1 className="font-display text-[28px] md:text-[36px] font-bold text-base-black dark:text-white mb-[8px]">
        {t('pricing.title')}
      </h1>
      <p className="font-body text-[16px] text-gray-900 dark:text-[#A0A0A3] mb-[16px] text-center">
        {t('pricing.subtitle')}
      </p>

      {/* Current plan status pill */}
      {isAuthenticated && (
        <div className="mb-[16px]">
          {isEnterprise ? (
            <span className="inline-flex items-center gap-[6px] px-[14px] py-[6px] rounded-[32px] bg-[#F0F9FF] text-[#7C3AED] font-body text-[13px] font-semibold border border-[#C4B5FD]">
              <span className="w-[6px] h-[6px] rounded-full bg-[#7C3AED]" />
              {t('pricing.currentPlan')}: Enterprise
            </span>
          ) : user?.isProGifted ? (
            <span className="inline-flex items-center gap-[6px] px-[14px] py-[6px] rounded-[32px] bg-[#FFFBEB] text-[#92400E] font-body text-[13px] font-semibold border border-[#FDE68A]">
              ⭐ Pro (Gifted)
            </span>
          ) : isTrial ? (
            <span className="inline-flex items-center gap-[6px] px-[14px] py-[6px] rounded-[32px] bg-[#F8FCFF] text-[#0ea5c9] font-body text-[13px] font-semibold border border-[#BCEBFB]">
              <span className="w-[6px] h-[6px] rounded-full bg-[#0ea5c9]" />
              {t('pricing.currentPlan')}: Free Trial
            </span>
          ) : isPro ? (
            <span className="inline-flex items-center gap-[6px] px-[14px] py-[6px] rounded-[32px] bg-[#1D1D1E] text-white font-body text-[13px] font-semibold">
              <span className="w-[6px] h-[6px] rounded-full bg-green-400" />
              {t('pricing.currentPlan')}: Pro ({user?.subscriptionTier})
            </span>
          ) : (
            <span className="inline-flex items-center gap-[6px] px-[14px] py-[6px] rounded-[32px] bg-[#EFEFF1] text-[#818185] font-body text-[13px] font-semibold">
              {t('pricing.currentPlan')}: {t('pricing.free')}
            </span>
          )}
        </div>
      )}

      {/* Billing toggle */}
      <div className="flex items-center gap-[4px] bg-[#EFEFF1] dark:bg-[#28282B] rounded-[32px] p-[4px] mb-[32px]">
        <button
          type="button"
          onClick={() => setBilling('monthly')}
          className={`px-[20px] py-[8px] rounded-[28px] font-body text-[13px] font-medium transition-all duration-200 ${
            billing === 'monthly'
              ? 'bg-white dark:bg-[#1C1C1F] text-base-black dark:text-white shadow-[0_1px_3px_rgba(0,0,0,0.1)]'
              : 'text-[#818185] hover:text-base-black dark:hover:text-white'
          }`}
        >
          Monthly
        </button>
        <button
          type="button"
          onClick={() => setBilling('annual')}
          className={`px-[20px] py-[8px] rounded-[28px] font-body text-[13px] font-medium transition-all duration-200 ${
            billing === 'annual'
              ? 'bg-white dark:bg-[#1C1C1F] text-base-black dark:text-white shadow-[0_1px_3px_rgba(0,0,0,0.1)]'
              : 'text-[#818185] hover:text-base-black dark:hover:text-white'
          }`}
        >
          Annual
          <span className="ml-[6px] text-[11px] text-[#0ea5c9] font-semibold">
            {t('pricing.savePerYear')}
          </span>
        </button>
      </div>

      {/* Trial banner */}
      {showTrial && (
        <div className="w-full max-w-[740px] mb-[20px] p-[16px] rounded-[12px] bg-[#F8FCFF] border border-[#BCEBFB] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-[12px]">
          <div>
            <p className="font-body text-[14px] font-semibold text-[#1D1D1E] dark:text-[#1D1D1E]">
              {t('pricing.freeTrial')}
            </p>
            <p className="font-body text-[13px] text-[#5A5A5E] dark:text-[#5A5A5E]">
              {t('pricing.freeTrialDesc')}
            </p>
          </div>
          <button
            type="button"
            onClick={handleTrialClick}
            disabled={trialPending}
            className={`flex-shrink-0 h-[40px] px-[20px] rounded-[32px] font-body text-[13px] font-medium transition-all duration-200 border border-[#0ea5c9] text-[#0ea5c9] hover:bg-[#0ea5c9] hover:text-white ${
              trialPending ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {trialPending ? t('pricing.redirecting') : t('pricing.getTrial')}
          </button>
        </div>
      )}

      {trialSuccessExpiry && (
        <TrialSuccessModal
          expiresAt={trialSuccessExpiry}
          onDismiss={() => {
            setTrialSuccessExpiry(null);
            navigate(ROUTER_LINKS.DASHBOARD);
          }}
        />
      )}

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-[16px] w-full max-w-[1080px]">
        {/* Free tier */}
        <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)] p-[24px] flex flex-col">
          <h3 className="font-body text-[18px] font-semibold text-base-black dark:text-white mb-[4px]">
            {t('pricing.free')}
          </h3>
          <div className="font-body text-[38px] font-bold text-base-black dark:text-white mb-[4px]">
            $0
            <span className="text-[16px] font-normal text-[#818185] dark:text-[#A0A0A3]">
              {t('pricing.perMonth')}
            </span>
          </div>
          <p className="font-body text-[13px] text-[#818185] dark:text-[#A0A0A3] mb-[20px]">
            {t('pricing.forIndividuals')}
          </p>

          <ul className="flex flex-col gap-[10px] mb-[24px] flex-1">
            {features.filter((f) => f.free !== false).map((f) => (
              <li key={f.label} className="flex items-center gap-[8px]">
                <span className="flex-shrink-0">{checkIcon}</span>
                <span className="font-body text-[13px] text-base-black dark:text-white">
                  {typeof f.free === 'string' ? `${f.free} ${f.label.toLowerCase()}` : f.label}
                </span>
              </li>
            ))}
          </ul>

          {isAuthenticated && !isPro && !isEnterprise ? (
            <div className="h-[44px] rounded-[32px] bg-[#EFEFF1] dark:bg-[#28282B] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] flex items-center justify-center font-body text-[14px] font-medium text-[#818185] dark:text-[#6B6B6F]">
              {t('pricing.currentPlan')}
            </div>
          ) : !isAuthenticated ? (
            <button
              type="button"
              onClick={handleFreeClick}
              className="h-[44px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[14px] font-medium text-base-black dark:text-white hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
            >
              {t('pricing.getStarted')}
            </button>
          ) : (
            <div className="h-[44px]" />
          )}
        </div>

        {/* Pro tier */}
        <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[#1D1D1E] dark:border-[#C8C8CB] shadow-[0_4px_20px_rgba(0,0,0,0.12)] p-[24px] flex flex-col relative">
          <div className="flex items-center gap-[8px] mb-[4px]">
            <h3 className="font-body text-[18px] font-semibold text-base-black dark:text-white">
              {t('pricing.pro')}
            </h3>
            {isAnnual && (
              <span className="bg-[#1D1D1E] text-white text-[10px] font-semibold uppercase tracking-wider px-[8px] py-[3px] rounded-[5px]">
                {t('pricing.popular')}
              </span>
            )}
          </div>
          <div className="flex items-baseline gap-[8px] mb-[4px]">
            <span className="font-body text-[38px] font-bold text-base-black dark:text-white">
              ${displayPrice}
            </span>
            <span className="font-body text-[16px] font-normal text-[#818185] dark:text-[#A0A0A3]">
              {t('pricing.perMonth')}
            </span>
          </div>
          <p className="font-body text-[13px] text-[#818185] dark:text-[#A0A0A3] mb-[20px]">
            {isAnnual
              ? t('pricing.billedAnnually', { amount: annualAmount })
              : t('pricing.forTeams')}
          </p>

          <ul className="flex flex-col gap-[10px] mb-[24px] flex-1">
            {features.filter((f) => f.pro !== false).map((f) => (
              <li key={f.label} className="flex items-center gap-[8px]">
                <span className="flex-shrink-0">{checkIcon}</span>
                <span className="font-body text-[13px] text-base-black dark:text-white">
                  {typeof f.pro === 'string' ? `${f.pro} ${f.label.toLowerCase()}` : f.label}
                </span>
              </li>
            ))}
          </ul>

          {isPro && !isTrial && !user?.isProGifted && !isEnterprise ? (
            <div className="h-[44px] rounded-[32px] bg-[#EFEFF1] dark:bg-[#28282B] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] flex items-center justify-center font-body text-[14px] font-medium text-[#818185] dark:text-[#6B6B6F]">
              {t('pricing.currentPlan')}
            </div>
          ) : (
            <button
              type="button"
              onClick={handleProClick}
              disabled={checkoutPending}
              className={`h-[44px] rounded-[32px] bg-base-black text-white font-body text-[14px] font-medium hover:bg-[#2E2E30] transition-all duration-200 hover:shadow-[0_0_0_4px_rgba(93,225,255,0.20)] ${
                checkoutPending ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {checkoutPending ? t('pricing.redirecting') : t('pricing.getStarted')}
            </button>
          )}
        </div>

        {/* Enterprise tier */}
        <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[#7C3AED] shadow-[0_4px_20px_rgba(124,58,237,0.12)] p-[24px] flex flex-col relative">
          <div className="flex items-center gap-[8px] mb-[4px]">
            <h3 className="font-body text-[18px] font-semibold text-base-black dark:text-white">
              Enterprise
            </h3>
            <span className="bg-[#7C3AED] text-white text-[10px] font-semibold uppercase tracking-wider px-[8px] py-[3px] rounded-[5px]">
              Custom
            </span>
          </div>
          <div className="font-body text-[38px] font-bold text-base-black dark:text-white mb-[4px]">
            Custom
          </div>
          <p className="font-body text-[13px] text-[#818185] dark:text-[#A0A0A3] mb-[20px]">
            For organizations that need branding, QR check-in, and full customization.
          </p>

          <ul className="flex flex-col gap-[10px] mb-[24px] flex-1">
            {features.map((f) => (
              <li key={f.label} className="flex items-center gap-[8px]">
                <span className="flex-shrink-0">{checkIcon}</span>
                <span className="font-body text-[13px] text-base-black dark:text-white">
                  {typeof f.enterprise === 'string' ? `${f.enterprise} ${f.label.toLowerCase()}` : f.label}
                </span>
              </li>
            ))}
          </ul>

          {isEnterprise ? (
            <div className="h-[44px] rounded-[32px] bg-[#F0F9FF] border border-[#C4B5FD] flex items-center justify-center font-body text-[14px] font-medium text-[#7C3AED]">
              {t('pricing.currentPlan')}
            </div>
          ) : (
            <div className="flex items-center gap-[10px]">
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(CONTACT_EMAIL); setEnterpriseCopied(true); setTimeout(() => setEnterpriseCopied(false), 2000); }}
                className="flex-1 h-[44px] rounded-[32px] bg-[#7C3AED] text-white font-body text-[14px] font-medium hover:bg-[#6D28D9] transition-all duration-200 flex items-center justify-center gap-[8px]"
              >
                {enterpriseCopied ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    Copied!
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                    Contact Us
                  </>
                )}
              </button>
              <a
                href={`mailto:${CONTACT_EMAIL}?subject=Enterprise%20Inquiry`}
                className="w-[44px] h-[44px] rounded-full border-[1.5px] border-[#C4B5FD] bg-[#F5F3FF] text-[#7C3AED] hover:bg-[#EDE9FE] hover:text-[#6D28D9] transition-all flex items-center justify-center flex-shrink-0"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </a>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};
